import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Upload,
  FileDown,
  ShoppingCart,
  RefreshCw,
  XCircle,
  AlertCircle,
} from "lucide-react";
import { setJobFilter } from "../../store/filtersSlice";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import ExportLocallyButton from "./ExportLocallyButton";
import { api } from "../../services/api";
import { EnrichmentJob } from "wasp/entities";
import { EnrichmentJobWithQuality } from "../../types/api";
import { resetExportJob } from "../../store/jobsSlice";
import { ExportJobService } from "../../services/job/ExportJobService";
import { useTranslation } from "../../i18n/hooks";

interface ExportControlsProps {
  job: EnrichmentJobWithQuality;
  isExporting: boolean;
  productCount: number;
  availableFeatures: Record<
    string,
    {
      label: string;
      changed: number;
      improvement: number;
      improvementText?: string;
    }
  >;
  onExportToShopify: (selectedFeatures: string[]) => void;
  progress?: number;
  status?: number;
  isAnyJobExporting: boolean;
  isFetching: boolean;
  isQualityLoading: boolean;
}

const ExportControls: React.FC<ExportControlsProps> = ({
  job,
  isExporting,
  productCount,
  availableFeatures,
  onExportToShopify,
  progress = 0,
  status,
  isAnyJobExporting,
  isFetching,
  isQualityLoading,
}) => {
  const dispatch = useDispatch();
  const { currentCollection } = useSelector((state: RootState) => state.user);
  const exportJobs = useSelector(
    (state: RootState) => state.jobs.activeExportJobs
  );
  const { t } = useTranslation();
  // Track selected features for export
  const [selectedFeatures, setSelectedFeatures] = useState<
    Record<string, boolean>
  >(
    Object.keys(availableFeatures).reduce(
      (acc, key) => ({
        ...acc,
        [key]: true,
      }),
      {}
    )
  );

  // const isExportPhase = status === 0 ? ;

  const exportIndicationText =
    status === 0
      ? t('exportControls.updating_products')
      : status === 1
        ? t('exportControls.exporting_to_shopify')
        : t('exportControls.syncing_with_shopify');
  const exportActionText =
    status === 0 ? t('exportControls.update') : status === 1 ? t('exportControls.export') : t('exportControls.sync');
  // Toggle all features
  const toggleAll = (checked: boolean) => {
    setSelectedFeatures(
      Object.keys(availableFeatures).reduce(
        (acc, key) => ({
          ...acc,
          [key]: checked,
        }),
        {}
      )
    );
  };

  // Show export button state based on conditions
  const renderExportButton = () => {
    if (isExporting) {
      return (
        <div className="flex items-center gap-2">
          <div className="relative flex items-center">
            <div
              className="absolute left-0 top-0 h-full bg-green-100 transition-all duration-300 rounded-lg"
              style={{ width: `${progress}%` }}
            />
            <button
              className="relative flex w-[120px] md:w-max items-center px-4 py-2 text-sm bg-green-50 text-green-700 rounded-lg"
              disabled
            >
              <RefreshCw className="w-4 h-4 animate-spin mr-2 flex-shrink-0" />
              <span className="hidden md:inline-block whitespace-nowrap">
                {exportIndicationText}
                {progress > 0 && ` (${progress}%)`}
              </span>
              <span className="md:hidden whitespace-nowrap">
                {exportActionText} {progress}%
              </span>
            </button>
          </div>

          <Dialog>
            <DialogTrigger asChild>
              <button
                className="flex items-center px-3 py-2 text-sm text-gray-500 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors border border-gray-200 hover:border-red-200"
                title={t('exportControls.cancel')}
              >
                <XCircle className="w-4 h-4" />
                <span className="hidden md:inline ml-2">{t('exportControls.cancel')}</span>
              </button>
            </DialogTrigger>
            <DialogContent className="z-[160]">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-amber-500" />
                  {t('exportControls.cancel_export_process')}
                </DialogTitle>
              </DialogHeader>
              <div className="py-4">
                {t('exportControls.cancel_export_description')}
              </div>
              <div className="flex justify-end">
                <Button
                  onClick={async () => {
                    if (!job.enrichmentJobId) return;
                    ExportJobService.getInstance().resetExportProgress(
                      job.enrichmentJobId,
                      job.id
                    );
                    dispatch(resetExportJob(job.enrichmentJobId));
                    dispatch(setJobFilter("all"));
                  }}
                  variant="destructive"
                >
                  {t('exportControls.yes_cancel_export')}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      );
    }

    return (
      <Dialog>
        <DialogTrigger asChild>
          <Button
            className="bg-green-50 text-green-700 hover:bg-green-100"
            disabled={isAnyJobExporting}
            data-testid="export-products-button"
          >
            {isFetching || isQualityLoading ? (
              <div className="flex items-center gap-2">
                <div className="h-4 w-24 bg-green-200/50 rounded animate-pulse" />
              </div>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                {t("exportControls.export_products", { count: productCount })}
              </>
            )}
          </Button>
        </DialogTrigger>

        <DialogContent className="max-w-2xl z-[150]">
          <DialogHeader>
            <DialogTitle>
              {t("exportControls.export_products_dialog_title", {
                count: productCount,
              })}
            </DialogTitle>
          </DialogHeader>

          <div className="mt-6">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle>
                    {t("exportControls.select_features_to_export")}
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleAll(true)}
                    >
                      {t("exportControls.select_all")}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleAll(false)}
                    >
                      {t("exportControls.clear_all")}
                    </Button>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="grid gap-4">
                {Object.entries(availableFeatures).map(([key, feature]) => (
                  <div
                    key={key}
                    className="flex items-center justify-between py-2 border-b border-gray-100"
                  >
                    <div className="flex items-center gap-6">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id={key}
                          checked={selectedFeatures[key]}
                          data-testid={`export-feature-checkbox-${key}`}
                          onCheckedChange={(checked) =>
                            setSelectedFeatures((prev) => ({
                              ...prev,
                              [key]: !!checked,
                            }))
                          }
                        />
                        <label htmlFor={key} className="text-sm font-medium">
                          {feature.label}
                        </label>
                      </div>
                      <div className="flex gap-2">
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                          {t("exportControls.changed_count", {
                            count: feature.changed,
                          })}
                        </span>
                        {feature.improvementText && (
                          <span className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded">
                            {feature.improvementText}
                          </span>
                        )}
                        {!feature.improvementText &&
                          feature.improvement !== 0 && (
                            <span className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded">
                              +{feature.improvement}%
                            </span>
                          )}
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <div className="flex justify-between mt-6">
              {job.enrichmentJobId && (
                <ExportLocallyButton enrichmentJobId={job.enrichmentJobId} />
              )}

              {currentCollection?.collection?.miqImportMethod === "shopify" && (
                <Button
                  onClick={() => {
                    dispatch(setJobFilter("running"));
                    onExportToShopify(
                      Object.keys(selectedFeatures).filter(
                        (key) => selectedFeatures[key]
                      )
                    );
                  }}
                  className="bg-green-600 text-white hover:bg-green-700"
                  disabled={!Object.values(selectedFeatures).some((v) => v)}
                >
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  {t("exportControls.export_to_shopify")}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  return renderExportButton();
};

export default ExportControls;
